import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import type {
  ProjectRow,
  ProjectWorkItem,
  ArtifactRow,
  WorkItemDependencyRow
} from '../../components/projects/types';
import { isCompleted, statusToPill } from '../../components/projects/helpers';

export interface ProjectCanvasData {
  project: ProjectRow;
  workItems: ReadonlyArray<ProjectWorkItem>;
  artifacts: ReadonlyArray<ArtifactRow>;
  leadName: string | null;
  /**
   * The open project's `work_item_dependencies` ("blocked by") edges — org-scoped
   * (active org) AND project-scoped (only edges whose blocked `work_item_id` is one
   * of this project's work-items). Empty when the project has no work-items (no
   * `.in([])` is issued). Net-new in 005 phase 1; feeds the ready-to-start
   * derivation via `projectReadySet`.
   */
  dependencies: ReadonlyArray<WorkItemDependencyRow>;
}

export type CanvasState = 'loading' | 'ready' | 'not-found' | 'error' | 'no-org';

export interface UseProjectCanvas {
  state: CanvasState;
  data: ProjectCanvasData | null;
  error: string | null;
  reload: () => void;
}

export function useProjectCanvas(
  activeOrgId: string | null,
  projectId: string | undefined
): UseProjectCanvas {
  const [state, setState] = useState<CanvasState>('loading');
  const [data, setData] = useState<ProjectCanvasData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Tracks the project we last loaded so a `reload()` (tick bump for the SAME
  // project) refetches silently — without flipping back to the loading state
  // and clearing data, which would unmount the page subtree (and any open
  // editor cover) before its close animation could play.
  const loadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeOrgId) {
      setState('no-org');
      return;
    }
    if (!projectId) {
      setState('not-found');
      return;
    }
    const key = `${activeOrgId}::${projectId}`;
    const silent = loadedKeyRef.current === key;
    loadedKeyRef.current = key;
    let cancelled = false;
    // First load / project switch shows the loading state; a same-project
    // reload swaps data in place (keeps the canvas + cover mounted).
    if (!silent) {
      setState('loading');
      setData(null);
    }
    setError(null);

    (async () => {
      const projectRes = await supabase
        .from('projects')
        .select(
          'id, org_id, name, description, status, lead_id, target_date, definition_of_done, created_at, created_by, updated_at, updated_by, archived_at'
        )
        .eq('id', projectId)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (projectRes.error) {
        setError(projectRes.error.message);
        setState('error');
        return;
      }
      if (!projectRes.data) {
        setState('not-found');
        return;
      }
      const project = projectRes.data as ProjectRow;

      const [wiRes, artRes, membersRes] = await Promise.all([
        supabase
          .from('work_items')
          .select(
            'id, org_id, name, type, status, rank, body, build_order, parent_work_item_id, project_id, assignee_id, priority, start_date, end_date, due_date, created_at, created_by, updated_at, updated_by'
          )
          .eq('org_id', activeOrgId)
          .eq('project_id', projectId)
          .order('rank', { ascending: true }),
        supabase
          .from('project_artifacts')
          .select('id, org_id, project_id, title, format, storage_path, created_at, created_by')
          .eq('org_id', activeOrgId)
          .eq('project_id', projectId)
          .order('created_at', { ascending: false }),
        supabase.rpc('list_org_members', { p_org_id: activeOrgId })
      ]);

      if (cancelled) return;

      const memberNames = new Map<string, string>();
      for (const m of (membersRes.data ?? []) as ReadonlyArray<Record<string, unknown>>) {
        const id = (m.user_id ?? m.id) as string | undefined;
        const name = (m.display_name ?? m.full_name ?? m.name ?? m.email) as string | undefined;
        if (id && name) memberNames.set(id, name);
      }

      const workItems: ProjectWorkItem[] = (wiRes.data ?? []).map((w) => ({
        ...(w as ProjectWorkItem),
        pill: statusToPill((w as { status: string }).status),
        isCompleted: isCompleted(w as ProjectWorkItem)
      }));

      // The open project's dependency edges — org-scoped AND project-scoped (only
      // edges whose blocked item belongs to this project). When the project has no
      // work-items there is nothing to scope to, so we skip the read entirely
      // (an empty `.in([])` would be wasteful) and surface `dependencies: []`.
      let dependencies: WorkItemDependencyRow[] = [];
      const workItemIds = workItems.map((w) => w.id);
      if (workItemIds.length > 0) {
        const depRes = await supabase
          .from('work_item_dependencies')
          .select('id, org_id, work_item_id, depends_on_work_item_id, created_at, created_by')
          .eq('org_id', activeOrgId)
          .in('work_item_id', workItemIds);

        if (cancelled) return;
        if (depRes.error) {
          setError(depRes.error.message);
          setState('error');
          return;
        }
        dependencies = (depRes.data ?? []) as WorkItemDependencyRow[];
      }

      setData({
        project,
        workItems,
        artifacts: (artRes.data ?? []) as ArtifactRow[],
        leadName: project.lead_id ? (memberNames.get(project.lead_id) ?? null) : null,
        dependencies
      });
      setState('ready');
    })().catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    });

    return () => {
      cancelled = true;
    };
  }, [activeOrgId, projectId, tick]);

  return { state, data, error, reload: () => setTick((t) => t + 1) };
}
